/** @jsx jsx */
import type { ReactNode } from 'react';
import { Box } from '@balance-web/box';
import { jsx } from '@balance-web/core';
import { useRawTheme, useTheme } from '@balance-web/theme';
import { getContrastText } from '@balance-web/utils';

import { useAvatarStackContext } from './context';
import type { AvatarSizeType, ShapeType } from './types';
import { getBaseStyles, getSizes, resolveAvatarSize } from './baseStyles';

export type BaseProps = {
  /** Does not support css. */
  css?: never;
  /** The color of the avatar. */
  color: string;
  /** The contents of the avatar or entity */
  children: ReactNode;
  /** When true, the avatar will be a dim neutral color. */
  inactive?: boolean;
  /** When true, the avatar will have a white background.  */
  guest?: boolean;
  /** The shape of the avatar. */
  shape: ShapeType;
  /** The size of the avatar. */
  size: AvatarSizeType;
};

export const Base = ({
  children,
  color,
  inactive,
  guest,
  shape,
  size,
  ...props
}: BaseProps) => {
  const theme = useRawTheme();
  const stackContext = useAvatarStackContext();

  // A stack sizes every avatar in it, and only ever with a token.
  const resolvedSize: AvatarSizeType = stackContext?.size || size;

  const { background, foreground, border } = useBaseColors({
    background: color,
    inactive,
    guest,
  });

  const avatarStyles = getBaseStyles({
    background,
    foreground,
    border,
    shape,
    size: resolveAvatarSize(getSizes(theme), resolvedSize),
  });

  return (
    <Box css={avatarStyles} {...props}>
      {children}
    </Box>
  );
};

// Utils
// ------------------------------

const useBaseColors = ({
  background: _background,
  inactive,
  guest,
}: {
  background: string;
  inactive?: boolean;
  guest?: boolean;
}) => {
  const { colors, semantic } = useTheme();

  let border = guest
    ? semantic.colour.surface.neutral.border
    : semantic.colour.surface.neutral.background;
  let background = _background;
  let foreground = inactive
    ? semantic.colour.text.textPrimary
    : getContrastText(_background);

  if (guest && !inactive) {
    background = colors.background;
    foreground = semantic.colour.text.textPrimary;
  } else if (guest && inactive) {
    background = semantic.colour.surface.neutral.background1;
    foreground = semantic.colour.text.textDisabled;
  } else if (inactive && !guest) {
    background = semantic.colour.surface.neutral.background1;
    foreground = semantic.colour.text.textDisabled;
  }

  return {
    background,
    foreground,
    border,
  };
};
